import { useState, useCallback } from "react";
import { useTranslation } from "react-i18next";
import type { VehicleSocket } from "../../../../../store/admin/vehiclesSocketStore";
import { useAlert } from "../../../../../context/AlertContext";

interface MediaRecorderContentProps {
  vehicle: VehicleSocket;
  isDarkMode: boolean;
  onClose?: () => void;
}

interface RecordingConfig {
  vehicleId: string;
  vehicleName: string;
  deviceId: string;
  latitude: number;
  longitude: number;
  speed?: number | null;
  engineStatus?: boolean | number | null;
  battery?: number | null;
  gps_sat?: number | null;
  recordingName: string;
  durationHours: number;
  status?: string;
  heading?: number | null;
}

function hasUsablePosition(v: VehicleSocket): boolean {
  return (
    typeof v.latitude === "number" &&
    typeof v.longitude === "number" &&
    !Number.isNaN(v.latitude) &&
    !Number.isNaN(v.longitude) &&
    (v.latitude !== 0 || v.longitude !== 0)
  );
}

export function MediaRecorderContent({ vehicle, isDarkMode }: MediaRecorderContentProps) {
  const { t } = useTranslation("lite");
  const { showAlert } = useAlert();

  const [showCreateModal, setShowCreateModal] = useState(false);
  const [recordingName, setRecordingName] = useState("");
  const [selectedDurationHours, setSelectedDurationHours] = useState(1);

  const openCreateModal = useCallback(() => {
    const defaultName = `${vehicle.vehicle?.name || vehicle.imei || vehicle.id} — ${new Date().toLocaleString()}`;
    setRecordingName(defaultName);
    setShowCreateModal(true);
  }, [vehicle]);

  const startRecordingInNewTab = useCallback(() => {
    if (!recordingName.trim()) {
      showAlert({
        title: t("error"),
        text: t("trackingReplayNameRequired") || "El nombre es obligatorio",
        icon: "error",
        confirmButtonText: "OK",
        confirmButtonColor: "#2563eb",
        onConfirm: () => {},
      });
      return;
    }

    // Create config object to pass to new tab
    const config: RecordingConfig = {
      vehicleId: vehicle.vehicle_id || vehicle.id,
      vehicleName: vehicle.vehicle?.name || vehicle.imei || vehicle.id,
      deviceId: vehicle.device?.id || vehicle.id,
      latitude: vehicle.latitude || 0,
      longitude: vehicle.longitude || 0,
      speed: vehicle.speed,
      engineStatus: vehicle.engineStatus,
      battery: vehicle.battery,
      gps_sat: vehicle.gps_sat,
      recordingName: recordingName.trim(),
      durationHours: selectedDurationHours,
      status: vehicle.status,
      heading: vehicle.course ?? null,
    };

    // Store config in sessionStorage with unique key
    const configKey = `tracking-replay-config-${Date.now()}`;
    sessionStorage.setItem(configKey, JSON.stringify(config));

    // Open new tab with the recording page
    const url = `/tracking-replay/recording?configKey=${encodeURIComponent(configKey)}`;
    window.open(url, "_blank", "noopener,noreferrer");

    // Close modal and reset state
    setShowCreateModal(false);
    setRecordingName("");
    setSelectedDurationHours(1);
  }, [recordingName, selectedDurationHours, vehicle, showAlert, t]);

  return (
    <>
      <div className={`mt-3 p-3 rounded-lg ${isDarkMode ? "bg-gray-800" : "bg-gray-100"}`}>
        <div className="space-y-3 mb-3">
          <div className="flex items-center justify-between gap-2">
            <span className={`text-sm font-medium ${isDarkMode ? "text-gray-200" : "text-gray-700"}`}>
              {t("vehicleCard.recordRoute") || "Grabar recorrido"}
            </span>
            <button
              type="button"
              onClick={openCreateModal}
              disabled={vehicle.status === "offline"}
              className={`px-4 py-2 text-white rounded-lg text-sm font-medium transition-colors flex items-center gap-2 shrink-0 ${
                vehicle.status === "offline" 
                  ? "bg-gray-400 cursor-not-allowed" 
                  : "bg-red-500 hover:bg-red-600"
              }`}
            >
              <i className="ri-circle-fill text-xs"></i>
              {t("vehicleCard.startRecording") || "Iniciar grabación"}
            </button>
          </div>
          <p className={`text-xs ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}>
            {t("vehicleCard.recordDescriptionNewTab")}
            <br />
            <br />
            <b>{t("vehicleCard.recordDescriptionNewTab1")}</b>
            <br />
            <br />
            <b>{t("vehicleCard.recordDescriptionNewTab2")}</b>
            <br />
            <br />
            <b>{t("vehicleCard.recordDescriptionNewTab3")}</b>
          </p>
          {!hasUsablePosition(vehicle) && (
            <p className="text-xs text-amber-600 dark:text-amber-400">
              {t("vehicleCard.recordNoGps") || "Este vehículo no tiene posición GPS válida todavía; los puntos se enviarán cuando haya datos."}
            </p>
          )}
          {vehicle.status === "offline" && (
            <p className="text-xs text-red-600 dark:text-red-400 mt-2">
              {t("vehicleCard.recordNoGps") || "Este vehículo está offline y no puede ser grabado. Espera a que se conecte para iniciar la grabación."}
            </p>
          )}
        </div>
      </div>

      {showCreateModal && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
          role="dialog"
          aria-modal="true"
          aria-labelledby="tracking-replay-create-title"
        >
          <div className={`rounded-lg p-6 w-full max-w-md ${isDarkMode ? "bg-gray-800" : "bg-white"}`}>
            <h3
              id="tracking-replay-create-title"
              className={`text-lg font-semibold mb-2 ${isDarkMode ? "text-white" : "text-gray-900"}`}
            >
              {t("trackingReplayCreateTitle") || "Nueva grabación de recorrido"}
            </h3>
            <p className={`text-xs mb-4 ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}>
              {t("trackingReplayCreateHintNewTab") ||
                "Se abrirá una nueva pestaña con el mapa donde podrás ver la grabación en tiempo real. La grabación se detendrá automáticamente al finalizar el tiempo o si cierras la pestaña."}
            </p>

            <label className={`block text-xs font-medium mb-1 ${isDarkMode ? "text-gray-300" : "text-gray-600"}`}>
              {t("trackingReplayName") || "Nombre del recorrido"}
            </label>
            <input
              type="text"
              value={recordingName}
              onChange={(e) => setRecordingName(e.target.value)}
              className={`w-full px-3 py-2 border rounded-lg mb-4 text-sm ${isDarkMode ? "bg-gray-700 border-gray-600 text-white" : "border-gray-300"}`}
              placeholder={t("enterName") || "Nombre"}
              autoFocus
            />

            <label className={`block text-xs font-medium mb-1 ${isDarkMode ? "text-gray-300" : "text-gray-600"}`}>
              {t("trackingReplayDuration") || "Duración de la grabación"}
            </label>
            <select
              value={selectedDurationHours}
              onChange={(e) => setSelectedDurationHours(Number(e.target.value))}
              className={`w-full px-3 py-2 border rounded-lg mb-4 text-sm ${isDarkMode ? "bg-gray-700 border-gray-600 text-white" : "border-gray-300"}`}
            >
              {Array.from({ length: 24 }, (_, i) => i + 1).map((hours) => (
                <option key={hours} value={hours}>
                  {hours} {hours === 1 ? (t("hour") || "hora") : (t("hours") || "horas")}
                </option>
              ))}
            </select>

            <div className={`text-xs mb-4 p-2 rounded ${isDarkMode ? "bg-gray-700/80 text-gray-300" : "bg-gray-50 text-gray-600"}`}>
              <span className="font-medium">{t("vehicleCard.vehicle") || "Vehículo"}: </span>
              {vehicle.vehicle?.name || vehicle.imei || vehicle.id}
            </div>

            <div className={`text-xs mb-4 p-2 rounded ${isDarkMode ? "bg-blue-900/30 text-blue-300" : "bg-blue-50 text-blue-700"}`}>
              <i className="ri-information-line mr-1"></i>
              {t("trackingReplayNewTabInfo") || "La grabación se realizará en una nueva pestaña. Puedes cerrar este panel y continuar usando la aplicación."}
            </div>

            <div className="flex gap-2">
              <button
                type="button"
                onClick={() => {
                  setShowCreateModal(false);
                  setRecordingName("");
                }}
                className="flex-1 px-4 py-2 bg-gray-500 text-white rounded-lg text-sm"
              >
                {t("cancel") || "Cancelar"}
              </button>
              <button
                type="button"
                onClick={startRecordingInNewTab}
                className="flex-1 px-4 py-2 bg-primary-500 text-white rounded-lg text-sm flex items-center justify-center gap-2"
              >
                <i className="ri-external-link-line"></i>
                {t("trackingReplayStartNewTab") || "Abrir y grabar"}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
